import React, { useState, useCallback, useRef, useEffect } from 'react';
import { TablaComisionesVista } from '../../vista/tablaComisionesVista';
import { useCargaMasivaMatriz } from '../useCargaMasivaMatriz';
import { mensajeBorradoFilaMatriz } from '../usoMatriz';
import { calcularComisionSugerida } from '../../vista/constantesTablaComisiones';
import type { IConfiguracionComisiones } from '../../interfaces';
import { useNotificaciones } from '../../../../globales/contextos/notificationContext';
import { useAvisoErrorConsulta } from '../useAvisoErrorConsulta';
import { EnumTipoNotificacion } from '../../../../globales/interfaces';
import { CotizadorModelo } from '../../../../modelos/CotizadorNBS';
import { useConfirmacion } from '../../../../globales/contextos/confirmacionContext';
import { useCargando } from '../../../../globales/contextos/cargandoContext';

interface ComisionesCVProps {
  datosIniciales?: IConfiguracionComisiones[];
  onRegistrarCancelacion?: (key: string, canceler: () => void) => void;
  onIniciarEdicionGeneral?: () => void;
}
import { usePaisConfiguracion } from '../paisConfiguracion';
import { esFilaNueva, idFilaNueva } from '../filaNuevaMatriz';

export const ComisionesCV: React.FC<ComisionesCVProps> = ({
  datosIniciales = [],
  onRegistrarCancelacion,
  onIniciarEdicionGeneral
}) => {
  const [comisiones, setComisiones] = useState<IConfiguracionComisiones[]>(datosIniciales);
  const [filaEnEdicion, setFilaEnEdicion] = useState<string | null>(null);
  const [respaldoFila, setRespaldoFila] = useState<any>(null);
  const [intentoGuardar, setIntentoGuardar] = useState(false);
  const { mostrarNotificacion } = useNotificaciones();
  const { confirmar } = useConfirmacion();
  const { manejarCargando } = useCargando();
  const paisId = usePaisConfiguracion();
  const avisarErrorConsulta = useAvisoErrorConsulta();
  // Se incrementa tras una carga masiva para volver a consultar la matriz.
  const [versionDatos, setVersionDatos] = useState(0);

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const modelo = new CotizadorModelo();
        const res = await modelo.obtenerMatrices<any>('comision', paisId as number);
        if (res?.Data) {
          setComisiones(res.Data.map((item: any) => ({ 
            Id: String(item.id || item.Id),
            Margen: item.margen || item.Margen,
            Comision: item.comisionPorcentaje || item.ComisionPorcentaje
          })));
        }
      } catch (error) {
        avisarErrorConsulta(error, 'la matriz de comisiones');
      }
    };
    cargarDatos();
  }, [paisId, versionDatos]);

  const mostrarNotificacionObligatorio = useCallback(() => {
    setIntentoGuardar(true);
    mostrarNotificacion(
      EnumTipoNotificacion.Warning,
      'Campos Obligatorios',
      'Debes diligenciar todos los campos obligatorios antes de guardar la fila.'
    );
  }, [mostrarNotificacion]);

  const respaldoFilaRef = useRef(respaldoFila);
  useEffect(() => {
    respaldoFilaRef.current = respaldoFila;
  }, [respaldoFila]);

  const cancelarEdicion = useCallback(() => {
    setIntentoGuardar(false);
    setFilaEnEdicion(null);
    const respaldo = respaldoFilaRef.current;
    if (respaldo) {
      if (respaldo.__esNueva || esFilaNueva(respaldo.Id)) {
        setComisiones(prev => prev.filter(f => f.Id !== respaldo.Id));
      } else {
        const { __esNueva, ...limpio } = respaldo;
        setComisiones(prev => prev.map(f => f.Id === limpio.Id ? limpio : f));
      }
      setRespaldoFila(null);
    }
  }, []);

  useEffect(() => {
    onRegistrarCancelacion?.('comision', cancelarEdicion);
  }, [onRegistrarCancelacion, cancelarEdicion]);

  const actualizarFila = useCallback((id: string, campo: string, valor: string | number) => {
    setComisiones(prev => prev.map(fila => {
      if (fila.Id === id) {
        const filaActualizada = { ...fila, [campo]: valor };

        // Al cambiar el margen, la comisión pasa a la calculada para ese margen; el usuario la puede cambiar después.
        if (campo === 'Margen' && valor !== "") {
          filaActualizada.Comision = calcularComisionSugerida(Number(valor) || 0);
        }

        return filaActualizada;
      }
      return fila;
    }));
  }, []);

  const eliminarFila = useCallback(async (id: string) => {
    // Cotizaciones abiertas cuyo margen promedio cae hoy en esta fila: se avisa antes de borrar.
    let mensaje = '¿Está seguro de que desea eliminar esta comisión? Esta acción no se puede deshacer.';
    if (!esFilaNueva(id)) {
      // Cargando mientras se consulta qué cotizaciones usan la fila.
      manejarCargando(true, undefined, true);
      try {
        const uso = (await new CotizadorModelo().obtenerUsoFilaMatriz('comision', Number(id))).Data ?? [];
        mensaje = mensajeBorradoFilaMatriz(uso,
          'Si la eliminas, al recalcularlas tomarán la comisión de la siguiente fila de margen menor (o 0% si no hay una menor).', mensaje);
      } catch (error) {
        console.error('No se pudo consultar el uso de la fila', error);
      } finally {
        manejarCargando(false);
      }
    }
    const confirmado = await confirmar({
      titulo: 'Eliminar fila',
      mensaje,
      textoAceptar: 'Sí, eliminar',
      tipo: EnumTipoNotificacion.Warning,
      opcionCancelar: true
    });

    if (confirmado) {
      try {
        const isNew = esFilaNueva(id);
        if (!isNew) {
          const modelo = new CotizadorModelo();
          await modelo.eliminarMatrizFila('comision', Number(id));
        }
        setComisiones(prev => prev.filter(fila => fila.Id !== id));
        mostrarNotificacion(EnumTipoNotificacion.Success, 'Fila Eliminada', 'La comisión se eliminó correctamente.');
      } catch (error) {
        console.error(error);
        mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', 'Ocurrió un error al eliminar la comisión.');
      }
    }
  }, [mostrarNotificacion, confirmar, manejarCargando]);

  const agregarFila = useCallback(() => {
    setIntentoGuardar(false);
    onIniciarEdicionGeneral?.();
    const nuevaFila: IConfiguracionComisiones = {
      Id: idFilaNueva(),
      Margen: "" as any, Comision: "" as any
    };
    setComisiones(prev => [nuevaFila, ...prev]);
    setRespaldoFila({ ...nuevaFila, __esNueva: true });
    setFilaEnEdicion(nuevaFila.Id);
  }, [onIniciarEdicionGeneral]);

  const guardarFila = useCallback(async (id: string) => {
    const filaToSave = comisiones.find(f => f.Id === id);
    if (!filaToSave) return;
    
    try {
      const modelo = new CotizadorModelo();
      const isNew = esFilaNueva(id);
      const dbId = isNew ? null : Number(id);

      const payload = {
        PaisId: paisId,
        Margen: Number(filaToSave.Margen),
        ComisionPorcentaje: Number(filaToSave.Comision)
      };

      const res = await modelo.guardarMatrizFila('comision', dbId, payload);

      if (res?.NotificationType === 1) { // Success
        if (isNew && res.Data) {
          setComisiones(prev => prev.map(f => f.Id === id ? { ...f, Id: String(res.Data) } : f));
        }
        setIntentoGuardar(false);
        setFilaEnEdicion(null);
        setRespaldoFila(null);
        mostrarNotificacion(EnumTipoNotificacion.Success, 'Fila Guardada', 'La comisión se guardó correctamente.');
      } else {
        mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', res?.Messages?.[0] || 'No se pudo guardar la fila.');
      }
    } catch (error: any) {
      console.error(error);
      mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', error?.mensajeBackend || 'Ocurrió un error inesperado al guardar la fila.');
    }
  }, [comisiones, mostrarNotificacion]);

  const iniciarEdicion = useCallback((fila: any) => {
    setIntentoGuardar(false);
    onIniciarEdicionGeneral?.();
    setRespaldoFila({ ...fila });
    setFilaEnEdicion(fila.Id);
  }, [onIniciarEdicionGeneral]);

  const { descargarPlantilla, cargaMasiva, modalResultado } = useCargaMasivaMatriz({
    matriz: 'comision',
    etiquetaReferencia: 'Margen',
    antesDeCargar: () => { if (filaEnEdicion) cancelarEdicion(); },
    alTerminar: () => setVersionDatos(v => v + 1)
  });

  return (
    <>
    {modalResultado}
    <TablaComisionesVista
      alRecargar={() => { cancelarEdicion(); setVersionDatos(v => v + 1); }}
      alDescargarPlantilla={descargarPlantilla}
      alCargaMasiva={cargaMasiva}
      datos={comisiones}
      actualizarFila={actualizarFila}
      eliminarFila={eliminarFila}
      guardarFila={guardarFila}
      filaEnEdicion={filaEnEdicion}
      iniciarEdicion={iniciarEdicion}
      cancelarEdicion={cancelarEdicion}
      agregarFila={agregarFila}
      mostrarNotificacionObligatorio={mostrarNotificacionObligatorio}
      intentoGuardar={intentoGuardar}
    />
    </>
  );
};
